import commaNumber from 'comma-number';
import { CircleHelp } from 'lucide-react';
import ordinal from 'ordinal';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type HTMLAttributes,
} from 'react';

import { Divider } from '@/components/base';
import Callout from '@/components/Callout/Callout';
import { cn } from '@/lib/cn';
import { SCHEDULE_PERIOD, WEEKDAY_MAP } from '@/types/commonConst';
import { getFeedbackSourceName } from '@/types/feedbackConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import type { ScrapingJob } from '../../api';
import {
  getFieldProperty,
  getIsItemPerDayField,
  getMaxItemCountByScraper,
  getScraperFieldList,
  getScraperOptionName,
  getScraperOptionsBySourceSafe,
} from '../../configs/config-actions';
import { SCRAPER_REQ_FIELD_KEY } from '../../configs/scraper-field-map/field-const';
import { SCRAPING_JOB_STATUS, SCRAPING_JOB_TYPE } from '../../types';
import { FeedbackCountGuide } from '../FeedbackCountGuide';
import PropertyNameLabel from '../PropertyNameLabel';

const KEYWORD_DETAIL_LIST_MAX_HEIGHT = 240;
const SCROLLABLE_DETAIL_CONTAINER_CLASSNAME =
  'rounded-md border border-gray-300 bg-gray-50 px-3 py-2';

const SCROLLABLE_DETAIL_MAX_HEIGHT_BY_FIELD: Partial<
  Record<SCRAPER_REQ_FIELD_KEY, number>
> = {
  [SCRAPER_REQ_FIELD_KEY.KEYWORD]: KEYWORD_DETAIL_LIST_MAX_HEIGHT,
  [SCRAPER_REQ_FIELD_KEY.DEFINITION]: KEYWORD_DETAIL_LIST_MAX_HEIGHT,
  [SCRAPER_REQ_FIELD_KEY.KEYWORDS]: KEYWORD_DETAIL_LIST_MAX_HEIGHT,
  [SCRAPER_REQ_FIELD_KEY.INCLUDE_KEYWORDS]: KEYWORD_DETAIL_LIST_MAX_HEIGHT,
  [SCRAPER_REQ_FIELD_KEY.EXCLUDE_KEYWORDS]: KEYWORD_DETAIL_LIST_MAX_HEIGHT,
};

export const ScrapingJobDetailContent = ({
  scrapingJob,
}: {
  scrapingJob: ScrapingJob;
}) => {
  const isItemPerDayField = getIsItemPerDayField(scrapingJob.scraper_name);
  const scraperFieldList = getScraperFieldList(scrapingJob.scraper_name);
  const scraperOptions = getScraperOptionsBySourceSafe(
    scrapingJob.source_type,
  );
  const hasDuplicated = scrapingJob.collect_count > scrapingJob.feedback_count;

  return (
    <div className="flex flex-col gap-8 w-full">
      {(scrapingJob.status === SCRAPING_JOB_STATUS.FAILED_CREATE_FEEDBACK ||
        scrapingJob.status === SCRAPING_JOB_STATUS.FAILED_SCRAPING) && (
        <Callout variant="error">
          Feedback scraping failed due to an unexpected error.
        </Callout>
      )}
      <div className="flex flex-col gap-6 w-full">
        <InfoRow>
          <PropertyNameLabel>Platform</PropertyNameLabel>
          <Value>{getFeedbackSourceName(scrapingJob?.source_type) || ''}</Value>
        </InfoRow>
        {scraperOptions.length > 1 && (
          <InfoRow>
            <PropertyNameLabel>Scraping by</PropertyNameLabel>
            <Value>{getScraperOptionName(scrapingJob.scraper_name)}</Value>
          </InfoRow>
        )}

        {scraperFieldList.map((field) => {
          const value =
            scrapingJob.scraper_input[
              field as keyof ScrapingJob['scraper_input']
            ];
          const property = getFieldProperty(field, scrapingJob.scraper_name);
          const propertyName = property?.name || field;
          if (typeof value === 'boolean') {
            return (
              <InfoRow key={field}>
                <PropertyNameLabel>{propertyName}</PropertyNameLabel>
                <Value>{value ? 'True' : 'False'}</Value>
              </InfoRow>
            );
          }
          if (Array.isArray(value)) {
            const detailListMaxHeight =
              SCROLLABLE_DETAIL_MAX_HEIGHT_BY_FIELD[field];
            const hasScrollableList = typeof detailListMaxHeight === 'number';
            return (
              <InfoRow key={field} className="items-start">
                <PropertyNameLabel>{propertyName}</PropertyNameLabel>
                <div
                  className={cn(
                    'flex flex-col gap-2 min-w-0',
                    hasScrollableList &&
                      `overflow-y-auto pr-1 ${SCROLLABLE_DETAIL_CONTAINER_CLASSNAME}`,
                  )}
                  style={
                    hasScrollableList
                      ? { maxHeight: detailListMaxHeight }
                      : undefined
                  }
                >
                  {value.map((item) => (
                    <Value key={item} className="flex-none">
                      {item}
                    </Value>
                  ))}
                </div>
              </InfoRow>
            );
          }
          if (typeof value === 'number' || typeof value === 'string') {
            const detailValueMaxHeight =
              SCROLLABLE_DETAIL_MAX_HEIGHT_BY_FIELD[field];
            const hasScrollableValue = typeof detailValueMaxHeight === 'number';
            return (
              <InfoRow key={field}>
                <PropertyNameLabel>{propertyName}</PropertyNameLabel>
                <Value
                  className={cn(
                    hasScrollableValue &&
                      `overflow-y-auto pr-1 whitespace-pre-wrap ${SCROLLABLE_DETAIL_CONTAINER_CLASSNAME}`,
                  )}
                  style={
                    hasScrollableValue
                      ? { maxHeight: detailValueMaxHeight }
                      : undefined
                  }
                >
                  {value}
                </Value>
              </InfoRow>
            );
          }
          return null;
        })}
        <Divider />
        {scrapingJob.type === SCRAPING_JOB_TYPE.SCHEDULED && (
          <InfoRow layout="vertical" className="items-start">
            <PropertyNameLabel>Schedule</PropertyNameLabel>
            {(function () {
              switch (scrapingJob.schedule?.period) {
                case SCHEDULE_PERIOD.DAILY:
                  return (
                    <Value>Everyday {scrapingJob.schedule?.hour}:00</Value>
                  );
                case SCHEDULE_PERIOD.WEEKLY: {
                  const weekday = scrapingJob.schedule?.weekday;
                  if (typeof weekday === 'undefined')
                    throw new Error('Weekday is undefined for weekly schedule');
                  return <Value>{WEEKDAY_MAP.get(weekday)}</Value>;
                }
                case SCHEDULE_PERIOD.MONTHLY: {
                  const date = scrapingJob.schedule?.date;
                  if (typeof date === 'undefined')
                    throw new Error('Date is undefined for monthly schedule');
                  return (
                    <Value>
                      Everymonth Day {ordinal(date)}{' '}
                      {scrapingJob.schedule?.hour}:00
                    </Value>
                  );
                }
                default:
                  throw new Error('Invalid schedule period');
              }
            })()}
          </InfoRow>
        )}
        {scrapingJob.type === SCRAPING_JOB_TYPE.ONETIME && (
          <>
            <InfoRow layout="vertical">
              <PropertyNameLabel>Created at</PropertyNameLabel>
              <Value>
                {getTimezoneDayjs(scrapingJob.created_at).format(
                  DATE_FORMAT.readableHour,
                )}
              </Value>
            </InfoRow>
            <InfoRow layout="vertical">
              <PropertyNameLabel>Date range</PropertyNameLabel>
              <Value>
                {getTimezoneDayjs(scrapingJob.start_date).format(
                  DATE_FORMAT.base,
                )}{' '}
                -{' '}
                {getTimezoneDayjs(scrapingJob.end_date).format(
                  DATE_FORMAT.base,
                )}
              </Value>
            </InfoRow>
          </>
        )}
        {isItemPerDayField ? (
          <InfoRow layout="vertical">
            <PropertyNameLabel className="w-full">
              Daily feedback collection limit
            </PropertyNameLabel>
            <Value>
              {scrapingJob.scraper_input.item_per_day
                ? commaNumber(scrapingJob.scraper_input.item_per_day)
                : commaNumber(
                    getMaxItemCountByScraper(scrapingJob.scraper_name),
                  )}
            </Value>
          </InfoRow>
        ) : (
          <InfoRow layout="vertical">
            <PropertyNameLabel className="w-full">
              Max feedback items to collect
            </PropertyNameLabel>
            <Value>
              {scrapingJob.scraper_input.max_items
                ? commaNumber(scrapingJob.scraper_input.max_items)
                : commaNumber(
                    getMaxItemCountByScraper(scrapingJob.scraper_name),
                  )}
            </Value>
          </InfoRow>
        )}

        <InfoRow layout="vertical">
          <PropertyNameLabel>Feedback count</PropertyNameLabel>
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex items-center gap-1">
              {commaNumber(scrapingJob.collect_count)}
              {hasDuplicated && (
                <FeedbackCountGuide scrapingJob={scrapingJob}>
                  <CircleHelp className="size-4 text-gray-700" />
                </FeedbackCountGuide>
              )}
            </div>
          </div>
        </InfoRow>
      </div>
    </div>
  );
};

const InfoRow = (
  props: HTMLAttributes<HTMLDivElement> & {
    layout?: 'horizontal' | 'vertical';
  },
) => {
  const { children, className, ...rest } = props;
  return (
    <div
      className={cn(
        'flex w-full items-start gap-2',
        className,
        props.layout === 'vertical' && 'flex-col items-start gap-2',
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

const Value = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<'p'>>(
  (props, ref) => {
    return (
      <p
        {...props}
        className={cn('flex-1 text-sm break-all', props.className)}
        ref={ref}
      />
    );
  },
);
